import styled from "@emotion/styled"
import { Container, Grid, Typography } from "@mui/material"
import * as api from "api"
import RatioElement from "common/ElementRatio"
import { useIsDesktop } from "common/MediaQueries"
import ResponsiveModelImage from "common/ResponsiveModelImage"
import VehicleChildDialogDesktop from "components/dialogs/VehicleChildDialogDesktop"
import VehicleChildDialogMobile from "components/dialogs/VehicleChildDialogMobile"
import { memo, useState } from "react"
import Shared from "theme/shared"

const Styled = {
  Wrapper: styled.div`
    background-color: ${(p) => p.theme.colors.paleGrey};
    padding: 20px 0 40px;
    ${(p) => p.theme.breakpoints.up("md")} {
      padding: 40px 27px 100px;
    }
    ${(p) => p.theme.breakpoints.down("sm")} {
      margin-top: 15px;
    }
  `,
  Header: styled(Shared.Header)`
    margin-bottom: 10px;
    ${(p) => p.theme.breakpoints.up("md")} {
      margin-bottom: 35px;
    }
  `,
  Grid: styled(Grid)`
    flex-direction: column;
    justify-content: center;
    margin-top: 5px;

    ${(p) => p.theme.breakpoints.down("sm")} {
      padding: 0 15px;
    }

    ${(p) => p.theme.breakpoints.up("md")} {
      margin-top: 45px;
      flex-direction: row;
    }
  `,
  ChildGrid: styled(Grid)`
    cursor: pointer;

    ${(p) => p.theme.breakpoints.down("sm")} {
      margin-top: 14px;
      :not(:first-of-type) {
        margin-top: 24px;
      }
    }

    ${(p) => p.theme.breakpoints.up("md")} {
      flex-direction: column;
    }
  `,
  InnerGrid: styled(Grid)`
    align-items: center;
    justify-content: center;
    padding: 8px 22px;
    padding-right: 0;
    ${(p) => p.theme.breakpoints.up("md")} {
      text-align: center;
    }

    ${(p) => p.theme.breakpoints.down("sm")} {
      border: solid 1px ${(p) => p.theme.colors.darkIndigo};
      box-shadow: 0 5px 10px 0 ${(p) => p.theme.colors.darkIndigo};
      background-color: ${(p) => p.theme.colors.white};
      .MuiTypography-root {
        text-align: left;
      }
    }
  `,
  Title: styled(Typography)`
    font-size: 26px;
    color: ${(p) => p.theme.colors.marine};
    ${(p) => p.theme.breakpoints.down("sm")} {
      line-height: 1;
    }
    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 34px;
    }
  `,
  Description: styled(Typography)`
    font-size: 14px;
    font-weight: 500;
    color: ${(p) => p.theme.colors.bluishGrey};
    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 18px;
      color: ${(p) => p.theme.colors.marine};
    }
  `,
}

type VehicleChild = api.VehicleChildren | api.VehicleFinishingLevel
type VehicleChildrenProps = {
  modelId: number
  modelName: string
  type: api.FamilyTypes
  data: VehicleChild[]
}

const VehicleChildren = ({
  data,
  modelId,
  modelName,
  type,
}: VehicleChildrenProps) => {
  const [childModel, setChildModel] = useState<VehicleChild>(null)
  const isDesktop = useIsDesktop()
  const header =
    type !== "FSeries"
      ? `מגוון דגמי ${modelName}`
      : `מגוון רמות גימור ${modelName}`
  return (
    <Styled.Wrapper>
      <Styled.Header>{header}</Styled.Header>
      <Shared.Mobile>
        <Shared.Divider />
      </Shared.Mobile>
      <Container>
        <Shared.Desktop>
          <Shared.Divider />
        </Shared.Desktop>
        <Styled.Grid container>
          {data.map((item, i) => (
            <Styled.ChildGrid
              item
              md={4}
              sm={12}
              key={i}
              onClick={() => setChildModel(item)}
            >
              <Styled.InnerGrid container>
                <Grid item xs={6} sm={6} md={12}>
                  <RatioElement ratio={16 / 9}>
                    <ResponsiveModelImage
                      image={item.image}
                      transparent
                      imageSizes={["135px", "373px"]}
                    />
                  </RatioElement>
                </Grid>
                <Grid item xs={6} sm={6} md={12}>
                  <Styled.Title>{item.name}</Styled.Title>
                  <Styled.Description>{item.bodyStyle}</Styled.Description>
                </Grid>
              </Styled.InnerGrid>
            </Styled.ChildGrid>
          ))}
        </Styled.Grid>
      </Container>
      {!isDesktop ? (
        <VehicleChildDialogMobile
          type={type}
          data={childModel}
          modelId={modelId}
          onClose={() => setChildModel(null)}
        />
      ) : (
        <VehicleChildDialogDesktop
          type={type}
          data={childModel}
          modelId={modelId}
          onClose={() => setChildModel(null)}
        />
      )}
    </Styled.Wrapper>
  )
}

export default memo(VehicleChildren)
